import {
  createContext,
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import IDirectMessage from '@th-types/direct-message.type';
import { IWorkerData } from '@th-types/worker.type';
import * as api from '@company/services/directMessage/api';
import { ISortableDirectMessages } from '@components/elements/DirectMessage/DirectMessageChat';
import {
  handleMarkAsReadOptimisticUpdate,
  handleMarkAsReadOptimisticRollback,
  handleMarkAsUnreadOptimisticUpdate,
  handleMarkAsUnreadOptimisticRollback,
} from '@utils/ComHubUtils';
import { DirectMessage } from '@company/types/comm-hub.type';
import QueryKeys from '@constants/queryKeys';

interface IDirectMessageContext {
  worker: IWorkerData | null;
  chatId: number | null;
  directMessages: IDirectMessage[];
  unsentMessages: IDirectMessage[];
  orderedMessages: ISortableDirectMessages[];
  originalDirectMessage: DirectMessage | null;
  setDirectMessages: (messages: IDirectMessage[]) => void;
  setUnsentMessages: (messages: IDirectMessage[]) => void;
  setChatId: (chatAssignmentId: number | null) => void;
  /**
   * Opens a direct message chat with a worker
   * @param originalMessage - The original message data (required for existing chats to enable optimistic updates when closing)
   * @param onSuccess - Optional callback invoked when the read API call succeeds
   */
  openDirectMessageChat: (
    person: IWorkerData,
    chatId: number | null,
    isPremiumCompany: boolean,
    originalMessage?: DirectMessage,
    onSuccess?: () => void
  ) => void;
  markAsRead: (chatId: number, onSuccess?: () => void) => void;
  clearDirectMessageData: () => void;
  markChatAsRead: (chatIdToRead: number, onSuccess?: () => void) => void;
  markAsUnread: (chatIdToUnread: number, onSuccess?: () => void) => void;
}

export const DirectMessageContext = createContext<IDirectMessageContext>({
  worker: null,
  chatId: null,
  directMessages: [],
  unsentMessages: [],
  orderedMessages: [],
  originalDirectMessage: null,
  setDirectMessages: () => {},
  setUnsentMessages: () => {},
  setChatId: () => {},
  openDirectMessageChat: () => {},
  markAsRead: () => {},
  clearDirectMessageData: () => {},
  markChatAsRead: () => {},
  markAsUnread: () => {},
});

interface ProviderProps {
  children: React.ReactNode;
}

export function DirectMessageProvider({ children }: ProviderProps) {
  const [worker, setWorker] = useState<IWorkerData | null>(null);
  const [chatId, setChatId] = useState<number | null>(null);
  const [directMessages, setDirectMessages] = useState<IDirectMessage[]>([]);
  const [unsentMessages, setUnsentMessages] = useState<IDirectMessage[]>([]);
  const [orderedMessages, setOrderedMessages] = useState<
    ISortableDirectMessages[]
  >([]);
  const [originalDirectMessage, setOriginalDirectMessage] =
    useState<DirectMessage | null>(null);

  const queryClient = useQueryClient();

  // Mutation for marking direct messages as read (with count update)
  const readDirectMessagesMutation = useMutation({
    mutationFn: api.readDirectMessages,
    onMutate: async (chatIdToRead: number) => {
      return handleMarkAsReadOptimisticUpdate(
        queryClient,
        chatIdToRead,
        'direct'
      );
    },
    onError: (err, chatIdToRead, context) => {
      if (context?.previousStates) {
        handleMarkAsReadOptimisticRollback(queryClient, context.previousStates);
      }
    },
    onSettled: () => {
      // Invalidate direct chat queries - use individual counter instead of indicators
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.DIRECT_CHAT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.DIRECT_CHAT_COUNT],
      });
    },
  });

  const unreadDirectMessagesMutation = useMutation({
    mutationFn: api.unreadDirectMessages,
    onMutate: async (chatIdToUnread: number) => {
      return handleMarkAsUnreadOptimisticUpdate(
        queryClient,
        chatIdToUnread,
        'direct'
      );
    },
    onError: (err, chatIdToUnread, context) => {
      if (context?.previousStates) {
        handleMarkAsUnreadOptimisticRollback(
          queryClient,
          context.previousStates
        );
      }
    },
    onSettled: () => {
      // Invalidate direct chat queries - use individual counter instead of indicators
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.DIRECT_CHAT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.DIRECT_CHAT_COUNT],
      });
    },
  });

  // Mutation for opening chat (list and count update)
  const openChatMutation = useMutation({
    mutationFn: api.readDirectMessages,
    onMutate: async (chatIdToRead: number) => {
      return handleMarkAsReadOptimisticUpdate(
        queryClient,
        chatIdToRead,
        'direct'
      );
    },
    onError: (err: any, chatIdToRead, context) => {
      if (context?.previousStates) {
        handleMarkAsReadOptimisticRollback(queryClient, context.previousStates);
      }
      // Log error but don't crash - the item may have been deleted
      console.error(
        `Failed to mark direct chat ${chatIdToRead} as read when opening:`,
        err?.response?.data || err
      );
    },
    onSettled: () => {
      // Invalidate direct chat count - use individual counter instead of indicators
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.DIRECT_CHAT_COUNT],
      });
    },
  });

  useEffect(() => {
    let chatMsgs: ISortableDirectMessages[] = directMessages.map((m) => ({
      id: m.id,
      created: m.dateCreated,
      createdTimestamp: new Date(m.dateCreated.toLocaleString()).valueOf(),
      message: m,
      sent: m.sent === undefined || m.sent === true,
    }));

    chatMsgs = chatMsgs.concat(
      unsentMessages.map((m) => ({
        id: m.id,
        created: m.dateCreated,
        createdTimestamp: new Date(m.dateCreated.toLocaleString()).valueOf(),
        systemMessage: m,
        sent: false,
      }))
    );
    const unsentList = chatMsgs
      .filter((x) => !x.sent)
      .sort(
        (a, b) =>
          new Date(a.createdTimestamp).getTime() -
          new Date(b.createdTimestamp).getTime()
      );

    const allSent = chatMsgs
      .filter((x) => x.sent)
      .sort(
        (a, b) =>
          new Date(a.createdTimestamp).getTime() -
          new Date(b.createdTimestamp).getTime()
      );

    const all = allSent.concat(unsentList);
    setOrderedMessages(all);
  }, [directMessages, setDirectMessages, unsentMessages]);

  const clearDirectMessageData = useCallback(() => {
    setWorker(null);
    setChatId(null);
    setOriginalDirectMessage(null);
  }, []);

  const openDirectMessageChat = useCallback(
    (
      person: IWorkerData,
      id: number | null,
      isPremiumCompany: boolean,
      originalMessage?: DirectMessage,
      onSuccess?: () => void
    ) => {
      if (!isPremiumCompany) {
        return;
      }
      setWorker(person);
      setChatId(id);
      setOriginalDirectMessage(originalMessage || null);
      // Call the read endpoint if there are unread messages
      if (originalMessage?.unreadMessages && id) {
        openChatMutation.mutate(id, { onSuccess });
      }
    },
    [openChatMutation]
  );

  const markAsRead = useCallback(
    (chatIdToRead: number, onSuccess?: () => void) => {
      readDirectMessagesMutation.mutate(chatIdToRead, { onSuccess });
    },
    [readDirectMessagesMutation]
  );

  const markChatAsRead = useCallback(
    (chatIdToRead: number, onSuccess?: () => void) => {
      if (chatIdToRead) {
        openChatMutation.mutate(chatIdToRead, { onSuccess });
      }
    },
    [openChatMutation]
  );

  const markAsUnread = useCallback(
    (chatIdToUnread: number, onSuccess?: () => void) => {
      unreadDirectMessagesMutation.mutate(chatIdToUnread, { onSuccess });
    },
    [unreadDirectMessagesMutation]
  );

  const contextValue: IDirectMessageContext = useMemo(() => {
    return {
      worker,
      chatId,
      directMessages,
      unsentMessages,
      orderedMessages,
      originalDirectMessage,
      setChatId,
      setDirectMessages,
      setUnsentMessages,
      openDirectMessageChat,
      markAsRead,
      clearDirectMessageData,
      markChatAsRead,
      markAsUnread,
    };
  }, [
    worker,
    chatId,
    directMessages,
    unsentMessages,
    orderedMessages,
    originalDirectMessage,
    setDirectMessages,
    setUnsentMessages,
    openDirectMessageChat,
    markAsRead,
    clearDirectMessageData,
    markChatAsRead,
    markAsUnread,
  ]);

  return (
    <DirectMessageContext.Provider value={contextValue}>
      {children}
    </DirectMessageContext.Provider>
  );
}

export function useDirectMessageContext() {
  const context: IDirectMessageContext = useContext(DirectMessageContext);
  if (context === undefined) {
    throw new Error(
      'useDirectMessageContext must be used within a DirectMessageProvider. Wrap a parent component in <DirectMessageProvider> to fix this error.'
    );
  }
  return context;
}
